/**
 * Forms Dashboard
 *
 * Layout (TaskManagement dashboard pattern):
 * - Header with actions
 * - Clickable stat tiles (live forms, entries this week, awaiting approval, waitlists)
 *   plus payment tiles only when money is involved
 * - Activity chart (views / starts / submissions, 30 days) + "Needs attention"
 * - Live form cards + recent entries feed (opens the entry quick view)
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconBroadcast,
  IconInbox,
  IconHourglassHigh,
  IconListNumbers,
  IconCreditCard,
  IconCash,
  IconPlus,
  IconRefresh,
  IconArrowRight,
  IconClock,
  IconPencil,
  IconUserQuestion,
  IconAlertTriangle,
  IconChevronRight,
  IconMoodSmile,
  IconForms,
  IconActivity,
} from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { Stack, Group, Button, Text, Title, SimpleGrid, Skeleton, Grid, Badge, ThemeIcon, Box, Alert } from '@/components/ui';
import { EmptyState } from '@/components/ui/data-display';
import ActionIcon from '@/components/ui/ActionIcon';
import { FadeIn } from '@/components/animations';
import AreaChartWidget from '@/components/advanced/Widgets/charts/AreaChartWidget';
import { useFormsService } from '@actschurch/ccms-sdk';
import { useFormsPermissions } from '@/hooks/forms/useFormsPermissions';
import {
  FormsStatTile,
  StatTilesSkeleton,
  SectionCard,
  FormCard,
  PersonCell,
  EntryStatusBadge,
  RelativeTime,
  EntryQuickView,
  formsKeys,
  formPath,
  formatDayLabel,
  formatMoney,
  errorMessage,
  entryPersonName,
  daysUntil,
  pct,
} from '../components';

interface AttentionItem {
  key: string;
  icon: React.ReactNode;
  color: string;
  text: string;
  count: number;
  onClick?: () => void;
}

export default function FormsDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const formsService = useFormsService();
  const perms = useFormsPermissions();
  const [quickEntryId, setQuickEntryId] = useState<number | null>(null);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: formsKeys.dashboard(),
    queryFn: () => formsService.getDashboard(),
  });

  // Every non-archived form the caller can see — for the attention panel and live cards.
  const { data: formsPage } = useQuery({
    queryKey: formsKeys.list({ dashboard: true }),
    queryFn: () => formsService.query({ pageSize: 100 }),
  });
  const forms = formsPage?.items ?? [];

  const chartData = useMemo(
    () =>
      (data?.last30Days ?? []).map((p) => ({
        day: formatDayLabel(p.day),
        Views: p.views,
        Started: p.starts,
        Submitted: p.submissions,
      })),
    [data?.last30Days]
  );
  const totals = useMemo(() => {
    const d = data?.last30Days ?? [];
    return {
      views: d.reduce((s, p) => s + p.views, 0),
      starts: d.reduce((s, p) => s + p.starts, 0),
      submissions: d.reduce((s, p) => s + p.submissions, 0),
    };
  }, [data?.last30Days]);

  const liveForms = useMemo(
    () =>
      forms
        .filter((f) => f.statusID === 2)
        .sort((a, b) => b.entryCount - a.entryCount)
        .slice(0, 4),
    [forms]
  );

  const attention: AttentionItem[] = useMemo(() => {
    if (!data) return [];
    const closingSoon = forms.filter((f) => {
      const n = f.statusID === 2 ? daysUntil(f.closesAt) : null;
      return n !== null && n >= 0 && n <= 7;
    });
    const unpublished = forms.filter((f) => f.hasUnpublishedChanges && f.publishedVersionNo > 0);
    const items: AttentionItem[] = [
      {
        key: 'approvals',
        icon: <IconHourglassHigh size={18} />,
        color: 'yellow',
        text: 'entries waiting for approval',
        count: data.pendingApprovals,
        onClick: perms.canApprove ? () => navigate('/app/forms/approvals') : undefined,
      },
      {
        key: 'payments',
        icon: <IconCreditCard size={18} />,
        color: 'orange',
        text: 'entries awaiting payment',
        count: data.pendingPayments,
      },
      {
        key: 'closing',
        icon: <IconClock size={18} />,
        color: 'red',
        text: `form${closingSoon.length === 1 ? '' : 's'} closing within a week`,
        count: closingSoon.length,
        onClick: () => navigate(closingSoon.length === 1 ? formPath(closingSoon[0].formID) : '/app/forms/forms'),
      },
      {
        key: 'unpublished',
        icon: <IconAlertTriangle size={18} />,
        color: 'yellow',
        text: `form${unpublished.length === 1 ? ' has' : 's have'} unpublished changes`,
        count: unpublished.length,
        onClick: () => navigate(unpublished.length === 1 ? formPath(unpublished[0].formID, 'builder') : '/app/forms/forms'),
      },
      {
        key: 'drafts',
        icon: <IconPencil size={18} />,
        color: 'gray',
        text: `draft${data.draftForms === 1 ? '' : 's'} not published yet`,
        count: data.draftForms,
        onClick: () => navigate('/app/forms/forms'),
      },
      {
        key: 'unlinked',
        icon: <IconUserQuestion size={18} />,
        color: 'grape',
        text: 'people not yet linked to a member record',
        count: data.unlinkedEntries,
      },
    ];
    return items.filter((i) => i.count > 0);
  }, [data, forms, perms.canApprove, navigate]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: formsKeys.all });

  if (isLoading) {
    return (
      <LayoutContainer>
        <FadeIn>
          <Stack gap="lg">
            <Group justify="space-between">
              <Skeleton height={40} width={260} />
              <Skeleton height={36} width={220} />
            </Group>
            <StatTilesSkeleton count={4} />
            <Grid>
              <Grid.Col span={{ base: 12, lg: 8 }}><Skeleton height={340} radius="md" /></Grid.Col>
              <Grid.Col span={{ base: 12, lg: 4 }}><Skeleton height={340} radius="md" /></Grid.Col>
            </Grid>
          </Stack>
        </FadeIn>
      </LayoutContainer>
    );
  }

  const showMoney = !!data && (data.pendingPayments > 0 || data.revenueThisMonth > 0);

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" wrap="wrap" align="flex-start">
            <div>
              <Title order={2}>Forms Dashboard</Title>
              <Text size="sm" c="dimmed">Registrations, sign-ups and surveys at a glance</Text>
            </div>
            <Group gap="sm">
              <ActionIcon variant="subtle" size="lg" onClick={refresh} loading={isFetching} aria-label="Refresh">
                <IconRefresh size={18} />
              </ActionIcon>
              <Button variant="light" leftSection={<IconForms size={16} />} onClick={() => navigate('/app/forms/forms')}>
                All forms
              </Button>
              {perms.canCreate && (
                <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/app/forms/forms/create')}>
                  New Form
                </Button>
              )}
            </Group>
          </Group>

          {error && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              {errorMessage(error, 'Failed to load the dashboard')}
            </Alert>
          )}

          {data && (
            <>
              <SimpleGrid cols={{ base: 2, md: 4 }}>
                <FormsStatTile
                  icon={<IconBroadcast size={22} />}
                  color="green"
                  label="Live forms"
                  value={data.publishedForms}
                  hint={`${data.draftForms} draft · ${data.totalForms} total`}
                  onClick={() => navigate('/app/forms/forms')}
                />
                <FormsStatTile
                  icon={<IconInbox size={22} />}
                  color="blue"
                  label="Entries this week"
                  value={data.entriesThisWeek}
                  hint={`${data.entriesToday} today · ${data.entriesThisMonth} this month`}
                />
                <FormsStatTile
                  icon={<IconHourglassHigh size={22} />}
                  color="yellow"
                  label="Awaiting approval"
                  value={data.pendingApprovals}
                  hint={data.pendingApprovals > 0 ? 'Open the approval queue' : 'Nothing waiting'}
                  emphasis={data.pendingApprovals > 0}
                  onClick={perms.canApprove ? () => navigate('/app/forms/approvals') : undefined}
                />
                <FormsStatTile
                  icon={<IconListNumbers size={22} />}
                  color="grape"
                  label="On waitlists"
                  value={data.waitlisted}
                  hint={data.waitlisted > 0 ? 'Waiting for a place' : 'No waitlists'}
                />
              </SimpleGrid>

              {showMoney && (
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <FormsStatTile
                    icon={<IconCreditCard size={22} />}
                    color="orange"
                    label="Awaiting payment"
                    value={data.pendingPayments}
                    emphasis={data.pendingPayments > 0}
                  />
                  <FormsStatTile icon={<IconCash size={22} />} color="teal" label="Revenue this month" value={formatMoney(data.revenueThisMonth)} />
                </SimpleGrid>
              )}

              <Grid gutter="lg">
                {/* Activity */}
                <Grid.Col span={{ base: 12, lg: 8 }}>
                  {totals.views + totals.submissions > 0 ? (
                    <Stack gap="sm">
                      <AreaChartWidget
                        title="Activity"
                        subtitle={`Last 30 days · ${totals.views.toLocaleString()} views → ${totals.submissions.toLocaleString()} entries (${pct(totals.submissions, totals.views)}%)`}
                        data={chartData}
                        xAxisKey="day"
                        height={290}
                        showGrid
                        showLegend
                        areas={[
                          { dataKey: 'Views', name: 'Views', color: 'var(--mantine-color-blue-4)', fillOpacity: 0.15 },
                          { dataKey: 'Started', name: 'Started', color: 'var(--mantine-color-violet-5)', fillOpacity: 0.15 },
                          { dataKey: 'Submitted', name: 'Submitted', color: 'var(--mantine-color-green-6)', fillOpacity: 0.3 },
                        ]}
                      />
                    </Stack>
                  ) : (
                    <SectionCard icon={<IconActivity size={18} />} title="Activity" description="Views and entries over the last 30 days" h="100%">
                      <EmptyState
                        size="sm"
                        icon={<IconInbox size={32} />}
                        title="No activity in the last 30 days"
                        description="Views and entries appear here once a form is shared."
                      />
                    </SectionCard>
                  )}
                </Grid.Col>

                {/* Needs attention */}
                <Grid.Col span={{ base: 12, lg: 4 }}>
                  <SectionCard
                    icon={<IconAlertTriangle size={18} />}
                    color="orange"
                    title="Needs attention"
                    description="Things worth a look today"
                    h="100%"
                  >
                    {attention.length === 0 ? (
                      <EmptyState size="sm" icon={<IconMoodSmile size={32} />} title="All caught up" description="Nothing needs your attention right now." />
                    ) : (
                      <Stack gap={6}>
                        {attention.map((a) => (
                          <Group
                            key={a.key}
                            wrap="nowrap"
                            gap="sm"
                            p="xs"
                            onClick={a.onClick}
                            style={{
                              borderRadius: 'var(--mantine-radius-md)',
                              cursor: a.onClick ? 'pointer' : 'default',
                              transition: 'background-color 120ms ease',
                            }}
                            onMouseEnter={(e) => {
                              if (a.onClick) (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--mantine-color-default-hover)';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLElement).style.backgroundColor = '';
                            }}
                          >
                            <ThemeIcon size={34} radius="md" variant="light" color={a.color} style={{ flexShrink: 0 }}>
                              {a.icon}
                            </ThemeIcon>
                            <Text size="sm" style={{ flex: 1, minWidth: 0 }}>
                              <Text span fw={700}>{a.count.toLocaleString()}</Text> {a.text}
                            </Text>
                            {a.onClick && <IconChevronRight size={16} color="var(--mantine-color-dimmed)" />}
                          </Group>
                        ))}
                      </Stack>
                    )}
                  </SectionCard>
                </Grid.Col>

                {/* Live forms */}
                <Grid.Col span={{ base: 12, lg: 8 }}>
                  <SectionCard
                    icon={<IconBroadcast size={18} />}
                    color="green"
                    title="Live forms"
                    description="Busiest published forms"
                    actions={
                      <Button variant="subtle" size="xs" rightSection={<IconArrowRight size={14} />} onClick={() => navigate('/app/forms/forms')}>
                        View all
                      </Button>
                    }
                  >
                    {liveForms.length === 0 ? (
                      <EmptyState
                        size="sm"
                        icon={<IconForms size={32} />}
                        title="No live forms"
                        description="Publish a form to start collecting entries."
                        actionLabel={perms.canCreate ? 'Create a form' : undefined}
                        onAction={perms.canCreate ? () => navigate('/app/forms/forms/create') : undefined}
                      />
                    ) : (
                      <SimpleGrid cols={{ base: 1, sm: 2 }}>
                        {liveForms.map((f) => (
                          <FormCard key={f.formID} form={f} onOpen={() => navigate(formPath(f.formID))} />
                        ))}
                      </SimpleGrid>
                    )}
                  </SectionCard>
                </Grid.Col>

                {/* Recent entries */}
                <Grid.Col span={{ base: 12, lg: 4 }}>
                  <SectionCard
                    icon={<IconInbox size={18} />}
                    title="Recent entries"
                    description="Latest across your forms"
                    actions={data.recentEntries.length > 0 ? <Badge variant="light">{data.entriesToday} today</Badge> : undefined}
                    h="100%"
                  >
                    {data.recentEntries.length === 0 ? (
                      <EmptyState size="sm" icon={<IconInbox size={32} />} title="No entries yet" description="New entries appear here as they arrive." />
                    ) : (
                      <Box mah={560} style={{ overflowY: 'auto' }}>
                        <Stack gap={4}>
                          {data.recentEntries.map((e) => {
                            const name = entryPersonName(e);
                            return (
                              <Group
                                key={e.formEntryID}
                                justify="space-between"
                                wrap="nowrap"
                                p="xs"
                                style={{ borderRadius: 'var(--mantine-radius-md)', cursor: 'pointer' }}
                                onClick={() => setQuickEntryId(e.formEntryID)}
                                onMouseEnter={(ev) => {
                                  (ev.currentTarget as HTMLElement).style.backgroundColor = 'var(--mantine-color-default-hover)';
                                }}
                                onMouseLeave={(ev) => {
                                  (ev.currentTarget as HTMLElement).style.backgroundColor = '';
                                }}
                              >
                                <Box style={{ minWidth: 0, flex: 1 }}>
                                  <PersonCell name={name} secondary={e.formName || e.publicRef} avatarKey={e.email || name} />
                                </Box>
                                <Stack gap={2} align="flex-end" style={{ flexShrink: 0 }}>
                                  <EntryStatusBadge statusId={e.statusID} name={e.statusName} withIcon={false} />
                                  <RelativeTime stamp={e.createdDateTime} size="xs" c="dimmed" />
                                </Stack>
                              </Group>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}
                  </SectionCard>
                </Grid.Col>
              </Grid>

            </>
          )}
        </Stack>
      </FadeIn>

      <EntryQuickView entryId={quickEntryId} onClose={() => setQuickEntryId(null)} />
    </LayoutContainer>
  );
}
